import { cn } from "@/lib/utils";
import { Maximize2, Minimize2, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

export type CanvasSize = "docked" | "expanded";

export type CanvasDocumentPresence = "open" | "closing";

export const CANVAS_CLOSE_MS = 300;

export function nextCanvasSize(size: CanvasSize): CanvasSize {
  return size === "expanded" ? "docked" : "expanded";
}

export function CanvasSplit({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="canvas-split"
      className={cn(
        "relative flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CanvasSplitThread({
  children,
  className,
  rail = false,
  collapsed = false,
}: {
  children: ReactNode;
  className?: string;
  rail?: boolean;
  /** When true, hide the chat column without unmounting (preserves composer state). */
  collapsed?: boolean;
}) {
  return (
    <div
      data-slot="canvas-split-thread"
      aria-hidden={collapsed || undefined}
      className={cn(
        "flex min-h-0 min-w-0 flex-col transition-[flex,width,opacity,max-width] duration-300 ease-out",
        collapsed
          ? "pointer-events-none w-0 max-w-0 flex-none overflow-hidden opacity-0"
          : "flex-1 opacity-100",
        !collapsed && rail && "lg:w-[min(100%,28rem)] lg:max-w-md lg:flex-none",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CanvasSplitDocument({
  children,
  className,
  presence = "open",
  expanded = false,
}: {
  children: ReactNode;
  className?: string;
  presence?: CanvasDocumentPresence;
  expanded?: boolean;
}) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (presence !== "open") {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(id);
  }, [presence]);

  const visible = presence === "open" && entered;
  const closing = presence === "closing";

  return (
    <section
      data-slot="canvas-split-document"
      data-state={presence}
      data-entered={visible ? "true" : "false"}
      className={cn(
        "flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-[rgba(15,23,42,0.09)] bg-[var(--surface-sunken)] shadow-[var(--shadow-card)] transition-[transform,opacity,flex,margin] duration-300 ease-out dark:border-white/10 dark:bg-slate-950 dark:shadow-none",
        // Mobile: full-area workspace overlay inside the chat shell
        "max-lg:absolute max-lg:inset-3 max-lg:z-20",
        "lg:m-5",
        expanded ? "lg:flex-1 lg:min-w-0" : "lg:min-w-[min(100%,28rem)] lg:flex-1",
        visible && !closing && "translate-x-0 opacity-100",
        (!visible || closing) && "pointer-events-none translate-x-4 opacity-0",
        className,
      )}
    >
      {children}
    </section>
  );
}

const SIZE_CONTROL: Record<CanvasSize, { label: string; Icon: typeof Maximize2 }> = {
  docked: { label: "Expand review", Icon: Maximize2 },
  expanded: { label: "Restore chat", Icon: Minimize2 },
};

const headerIconButtonClassName =
  "rounded-md p-1.5 text-[#576274] transition-colors hover:bg-[var(--lm-hover)] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(124,58,237,0.45)] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white";

export function CanvasSplitHeader({
  title,
  size = "docked",
  onSize,
  onClose,
}: {
  title: string;
  size?: CanvasSize;
  onSize?: () => void;
  onClose?: () => void;
}) {
  const control = SIZE_CONTROL[size];
  const SizeIcon = control.Icon;
  return (
    <div
      data-slot="canvas-split-header"
      className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(15,23,42,0.09)] bg-[var(--chat-shell-bg)] px-3 py-2.5 dark:border-white/10 dark:bg-slate-900/90"
    >
      <h2
        className="min-w-0 truncate text-sm font-semibold tracking-tight text-[#101828] dark:text-white"
        title={title}
      >
        {title}
      </h2>
      <div className="flex shrink-0 items-center gap-0.5">
        {onSize ? (
          <button
            type="button"
            onClick={onSize}
            aria-label={control.label}
            title={control.label}
            className={headerIconButtonClassName}
          >
            <SizeIcon className="h-4 w-4" />
          </button>
        ) : null}
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close review"
            title="Close"
            className={headerIconButtonClassName}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function CanvasSplitBody({
  children,
  writing = false,
  className,
}: {
  children: ReactNode;
  writing?: boolean;
  className?: string;
}) {
  return (
    <div
      data-slot="canvas-split-body"
      className={cn("min-h-0 flex-1 overflow-auto px-4 pb-4 sm:px-5 sm:pb-5", className)}
    >
      {children}
      {writing ? (
        <span className="mt-2 inline-block h-4 w-0.5 animate-pulse bg-primary" aria-hidden />
      ) : null}
    </div>
  );
}
